import { useEffect, useState } from "react";
import { ListChecks } from "lucide-react";
import { api } from "@/data/api";
import { useAsync } from "@/hooks/useAsync";
import { PageHeader, StatusBadge, DataTable, AsyncBoundary, RowsSkeleton, EmptyState } from "@/components/common";

const ACTIVE = new Set(["pending", "dispatched", "running"]);

export default function Jobs() {
  const [refresh, setRefresh] = useState(0);
  const { data, loading, error } = useAsync(() => api.getJobs(), [refresh], 20000);
  useEffect(() => {
    if (!data?.some((job) => ACTIVE.has(job.status))) return undefined;
    const timer = setInterval(() => setRefresh((value) => value + 1), 4000);
    return () => clearInterval(timer);
  }, [data]);

  const rows = (data || []).map((job) => ({
    ...job,
    operation: job.type,
    resource: job.resource_type ? `${job.resource_type} · ${job.resource_id}` : "—",
    server: job.server_hostname || "—",
    actor: job.requested_by_name || "Sistema",
    created: job.created_at ? new Date(job.created_at).toLocaleString("pt-BR") : "—",
    started: job.started_at ? new Date(job.started_at).toLocaleString("pt-BR") : "—",
    finished: job.finished_at ? new Date(job.finished_at).toLocaleString("pt-BR") : "—",
    duration: job.duration_seconds == null ? "—" : `${job.duration_seconds}s`,
    outcome: job.result?.error || job.result?.message || "—",
  }));

  return <div className="space-y-6 animate-fade-up">
    <PageHeader icon={ListChecks} title="Jobs" description="Operações reais enviadas ao Agent e seus resultados."
      breadcrumb={<><span>Infraestrutura</span><span>/</span><span className="text-foreground">Jobs</span></>} />
    <AsyncBoundary loading={loading} error={error} skeleton={<RowsSkeleton rows={8} />}>
      {rows.length ? <DataTable testid="jobs-table" rows={rows} keyField="id" columns={[
        { key: "operation", header: "Operação", render: (row) => <span className="font-mono text-xs">{row.operation}</span> },
        { key: "status", header: "Status", render: (row) => <StatusBadge status={row.status} size="sm" /> },
        { key: "resource", header: "Recurso", render: (row) => <span className="font-mono text-xs">{row.resource}</span> },
        { key: "server", header: "Servidor" }, { key: "actor", header: "Solicitado por" },
        { key: "created", header: "Criado" }, { key: "started", header: "Iniciado" },
        { key: "finished", header: "Finalizado" }, { key: "duration", header: "Duração" },
        { key: "outcome", header: "Resultado", render: (row) => row.status === "succeeded" ? "—" : <span className="text-xs text-err">{row.outcome}</span> },
      ]} /> : <EmptyState icon={ListChecks} title="Nenhum Job registrado" description="As operações reais aparecerão aqui quando um Agent receber uma solicitação." />}
    </AsyncBoundary>
  </div>;
}
